// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';

import { useI18n } from '@/i18n';

import { MAX_VIEW_ZOOM, MIN_VIEW_ZOOM } from './tileMath';

/** Floating-point slack when asking "is this zoomed in at all?" */
const ZOOM_EPSILON = 0.001;

export type ZoomControlsProps = {
  zoom: number;
  minZoom?: number;
  maxZoom?: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  /** Returns to the framing that fits the subject. Hidden while already there. */
  onReset: () => void;
  /** Distinguishes the two instances on the course-analysis screen for E2E. */
  testIDPrefix: string;
};

/**
 * The same +/- /fit cluster the in-round hole view offers, lifted out so the course-analysis
 * screen's two views — the imagery and the derived guide drawing — can each carry one while
 * both are driven from state their parent owns and keeps in sync.
 */
export function ZoomControls({
  zoom,
  minZoom = MIN_VIEW_ZOOM,
  maxZoom = MAX_VIEW_ZOOM,
  onZoomIn,
  onZoomOut,
  onReset,
  testIDPrefix,
}: ZoomControlsProps) {
  const { t } = useI18n();
  const zoomedIn = zoom > minZoom + ZOOM_EPSILON;

  return (
    <View style={styles.controls} pointerEvents="box-none">
      <Pressable
        accessibilityRole="button"
        testID={`${testIDPrefix}-zoom-in`}
        style={styles.button}
        disabled={zoom >= maxZoom}
        onPress={onZoomIn}
      >
        <Text style={styles.buttonText}>+</Text>
      </Pressable>
      <Pressable
        accessibilityRole="button"
        testID={`${testIDPrefix}-zoom-out`}
        style={styles.button}
        disabled={!zoomedIn}
        onPress={onZoomOut}
      >
        <Text style={styles.buttonText}>−</Text>
      </Pressable>
      {zoomedIn ? (
        <Pressable
          accessibilityRole="button"
          testID={`${testIDPrefix}-zoom-fit`}
          style={styles.button}
          onPress={onReset}
        >
          <Text style={styles.fitText}>{t('hole.holeViewZoomFit')}</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  controls: {
    position: 'absolute',
    right: 8,
    top: 8,
    alignItems: 'flex-end',
    gap: 6,
  },
  button: {
    minWidth: 30,
    height: 30,
    paddingHorizontal: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(245,245,220,0.3)',
    backgroundColor: 'rgba(1,24,6,0.62)',
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({ web: { cursor: 'pointer' }, default: {} }),
  },
  buttonText: {
    color: '#F5F5DC',
    fontSize: 18,
    fontWeight: '800',
    lineHeight: 20,
  },
  fitText: {
    color: '#D8DCC8',
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
});

export default ZoomControls;
